import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useApiStable from "../../utils/useApiStable";
import { useAuth } from "../../context/AuthContext";
import { isUserAdmin } from "../../utils/userUtils";

const LeagueTeamList = () => {
    const api = useApiStable();
    const { user } = useAuth();
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [syncing, setSyncing] = useState(false);

    useEffect(() => {
        api.get("/league/teams")
            .then(setTeams)
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    const handleSync = async () => {
        setSyncing(true);
        try {
            await api.post("/league/teams/sync");
            const updated = await api.get("/league/teams");
            setTeams(updated);
        } catch (err) {
            setError(err.message);
        } finally {
            setSyncing(false);
        }
    };

    const toggleFollow = async (team, e) => {
        // Karte ist ein Link – Klick auf den Button darf nicht navigieren.
        e.preventDefault();
        e.stopPropagation();
        try {
            const updated = team.followedByCurrentUser
                ? await api.delete(`/league/teams/${team.id}/follow`)
                : await api.post(`/league/teams/${team.id}/follow`);
            setTeams(prev => prev.map(t => (t.id === updated.id ? updated : t)));
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) return <div className="groupphase-section"><p>Lade Mannschaften...</p></div>;
    if (error) return <div className="groupphase-section"><p style={{ color: "red" }}>Fehler: {error}</p></div>;

    const isPlayer = (team) => user && team.players.some(p => p.uuid === user.personUuid);
    const myTeams = teams.filter(team =>
        team.captainForCurrentUser || team.followedByCurrentUser || isPlayer(team)
    );
    const otherTeams = teams.filter(team => !myTeams.includes(team));

    const renderTeamList = (teamList, title) => {
        if (teamList.length === 0) return null;

        const grouped = teamList.reduce((acc, team) => {
            const season = team.season || "Unbekannte Saison";
            if (!acc[season]) acc[season] = [];
            acc[season].push(team);
            return acc;
        }, {});

        return (
            <div style={{ marginBottom: 32 }}>
                {title && <h2 style={{ fontSize: 18, marginBottom: 16, borderBottom: "2px solid #eee", paddingBottom: 8 }}>{title}</h2>}
                {Object.entries(grouped)
                    .sort(([a], [b]) => b.localeCompare(a))
                    .map(([season, seasonTeams]) => (
                        <div key={season} style={{ marginBottom: 24 }}>
                            <h3 style={{ fontSize: 16, color: "#555", marginBottom: 8 }}>Saison {season}</h3>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {seasonTeams.map(team => (
                                    <Link
                                        key={team.id}
                                        to={`/mannschaften/${team.id}`}
                                        style={{ textDecoration: "none" }}
                                    >
                                        <div
                                            style={{
                                                padding: "12px 16px",
                                                border: "1px solid #ddd",
                                                borderRadius: 6,
                                                backgroundColor: "#fff",
                                                cursor: "pointer",
                                                transition: "background 0.15s",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                gap: 12,
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f5f5f5"}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = "#fff"}
                                        >
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontWeight: "bold", fontSize: 16, color: "#333" }}>
                                                    {team.name}
                                                </div>
                                                <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>
                                                    {team.upcomingMatchCount > 0
                                                        ? `${team.upcomingMatchCount} anstehende${team.upcomingMatchCount === 1 ? 's Spiel' : ' Spiele'}`
                                                        : 'Keine anstehenden Spiele'}
                                                    {team.tablePosition != null && (
                                                        <span style={{ marginLeft: 12 }}>
                                                            · {team.tablePosition}. Platz
                                                            {team.tablePoints && ` (${team.tablePoints} Pkt.)`}
                                                        </span>
                                                    )}
                                                    {team.captainsName && (
                                                        <span style={{ marginLeft: 12 }}>
                                                            · MF: {team.captainsName}
                                                        </span>
                                                    )}
                                                    {team.captainForCurrentUser && (
                                                        <span
                                                            className="groupphase-status-badge groupphase-status-badge--success"
                                                            style={{ marginLeft: 8 }}
                                                        >
                                                            Mannschaftsführer
                                                        </span>
                                                    )}
                                                    {team.followedByCurrentUser && !team.captainForCurrentUser && !isPlayer(team) && (
                                                        <span
                                                            className="groupphase-status-badge"
                                                            style={{ marginLeft: 8 }}
                                                        >
                                                            Gefolgt
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {user && !team.captainForCurrentUser && !isPlayer(team) && (
                                                <button
                                                    onClick={e => toggleFollow(team, e)}
                                                    style={{
                                                        flexShrink: 0,
                                                        padding: "6px 12px",
                                                        fontSize: 13,
                                                        cursor: "pointer",
                                                        border: team.followedByCurrentUser ? "1px solid #bbb" : "1px solid #4CAF50",
                                                        borderRadius: 6,
                                                        backgroundColor: team.followedByCurrentUser ? "#f5f5f5" : "#4CAF50",
                                                        color: team.followedByCurrentUser ? "#555" : "#fff",
                                                    }}
                                                >
                                                    {team.followedByCurrentUser ? "Nicht mehr folgen" : "Folgen"}
                                                </button>
                                            )}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}
            </div>
        );
    };

    return (
        <div className="groupphase-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h2>Mannschaften</h2>
                {isUserAdmin(user) && (
                    <button
                        onClick={handleSync}
                        disabled={syncing}
                        style={{ padding: "6px 14px", fontSize: 13, cursor: "pointer" }}
                    >
                        {syncing ? "Synchronisiere..." : "Von TNB synchronisieren"}
                    </button>
                )}
            </div>

            {teams.length === 0 ? (
                <p style={{ color: "#666", fontStyle: "italic" }}>
                    Noch keine Mannschaften hinterlegt.
                    {isUserAdmin(user) && " Klicken Sie auf \"Von TNB synchronisieren\", um Mannschaften zu importieren."}
                </p>
            ) : (
                <>
                    {renderTeamList(myTeams, "Meine Mannschaften")}
                    {renderTeamList(otherTeams, myTeams.length > 0 ? "Weitere Mannschaften" : null)}
                </>
            )}
        </div>
    );
};

export default LeagueTeamList;
